/**
 * Country Redirection Script
 * 
 * Implements automatic country-based redirection and Orbe modal control.
 * 
 * Priority order:
 * 1. Cookie mdApp_countryCodeDomain (manual selection) - ABSOLUTE PRIORITY
 * 2. If no cookie, geolocation: Shopify endpoint first, then Orbe
 * 
 * @see https://help.orbe.app/developer-reference/javascript
 */

(function() {
  'use strict';

  // Configuration from Liquid (set in theme.liquid <head>)
  const config = {
    enabled: window.redirectionEnabled || false,
    currentMarketHandle: window.currentMarketHandle || '',
    shopDomain: window.shopDomain || '',
    excludedCountries: window.redirectionExcludedCountries || 'es:es,us:row'
  };

  // Cookie name for Orbe manual selection
  const ORBE_COOKIE_NAME = 'mdApp_countryCodeDomain';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 60; // 60 days in seconds

  /**
   * Cookie Helper Functions
   */

  /**
   * Read a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  /**
   * Set a cookie following Orbe's pattern
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {string} domain - Domain for the cookie
   * @param {number} maxAge - Max age in seconds
   */
  function setCookie(name, value, domain, maxAge) {
    document.cookie = `${name}=${value}; domain=${domain}; path=/; max-age=${maxAge};`;
  }

  /**
   * Geolocation Functions
   */

  /**
   * Get country code from Shopify browsing context suggestions endpoint
   * @returns {Promise<string|null>} Country code or null if unavailable
   */
  async function getCountryFromShopify() {
    try {
      const response = await fetch('/browsing_context_suggestions.json', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      // Extract country code from response
      // Response format: { detected_values: { country: { handle: "ES", name: "España" } } }
      if (data.detected_values && data.detected_values.country && data.detected_values.country.handle) {
        return data.detected_values.country.handle.toLowerCase();
      }

      return null;
    } catch (error) {
      // Silently fail - will fallback to Orbe
      return null;
    }
  }

  /**
   * Get country code from Orbe's JavaScript API
   * @returns {Promise<string|null>} Country code or null if unavailable
   */
  function getCountryFromOrbe() {
    return new Promise((resolve) => {
      // Orbe may take time to load, use setTimeout as per documentation
      setTimeout(() => {
        try {
          // Check if orbito object exists and has the method
          if (typeof orbito !== 'undefined' && typeof orbito.getRemoteCountryCode === 'function') {
            orbito.getRemoteCountryCode()
              .then((value) => {
                if (value && typeof value === 'string') {
                  resolve(value.toLowerCase());
                } else {
                  resolve(null);
                }
              })
              .catch(() => {
                resolve(null);
              });
          } else {
            resolve(null);
          }
        } catch (error) {
          resolve(null);
        }
      }, 1000); // Wait 1 second for Orbe to load
    });
  }

  /**
   * Get user's country code from available sources (Shopify first, then Orbe)
   * @returns {Promise<string|null>} Country code or null if unavailable
   */
  async function getUserCountry() {
    // Try Shopify endpoint first
    const shopifyCountry = await getCountryFromShopify();

    if (shopifyCountry) {
      return shopifyCountry;
    }

    // Fallback to Orbe
    const orbeCountry = await getCountryFromOrbe();
    if (orbeCountry) {
      return orbeCountry;
    }

    return null;
  }

  /**
   * Configuration Parsing
   */

  /**
   * Parse excluded countries string into a Map
   * Format: "es:es,us:row" -> Map { 'es' => 'es', 'us' => 'row' }
   * @param {string} str - Excluded countries string
   * @returns {Map<string, string>} Map of country code to market handle
   */
  function parseExcludedCountries(str) {
    const map = new Map();
    if (!str || typeof str !== 'string') {
      return map;
    }

    const pairs = str.split(',');
    pairs.forEach(pair => {
      const trimmed = pair.trim();
      if (trimmed) {
        const [country, market] = trimmed.split(':');
        if (country && market) {
          map.set(country.trim().toLowerCase(), market.trim().toLowerCase());
        }
      }
    });

    return map;
  }

  /**
   * Redirection Functions
   */

  /**
   * Redirect to the same URL adding/updating the country query parameter
   * @param {string} country - Country code to add to URL
   */
  function redirectWithCountry(country) {
    if (!country) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('country', country);
    
    // Create/update cookie before redirect
    setCookie(ORBE_COOKIE_NAME, country, config.shopDomain, COOKIE_MAX_AGE);
    
    // Perform redirect
    window.location.href = url.toString();
  }

  /**
   * Orbe Modal Control Functions
   */

  // MutationObserver that prevents orbe--opened from blocking Klaviyo
  // when Orbe's modal is hidden by us (hidden ≠ closed).
  let orbeClassGuard = null;

  /**
   * Start watching body class changes.
   * If Orbe adds orbe--opened while the modal is hidden (orbe-modal-hidden present),
   * remove orbe--opened immediately so it doesn't suppress Klaviyo.
   */
  function startOrbeClassGuard() {
    orbeClassGuard = new MutationObserver(function() {
      if (
        document.body.classList.contains('orbe-modal-hidden') &&
        document.body.classList.contains('orbe--opened')
      ) {
        document.body.classList.remove('orbe--opened');
      }
    });
    orbeClassGuard.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Stop watching body class changes.
   * Called when we decide the Orbe modal should actually be visible.
   */
  function stopOrbeClassGuard() {
    if (orbeClassGuard) {
      orbeClassGuard.disconnect();
      orbeClassGuard = null;
    }
  }

  /**
   * Show Orbe modal by removing the hidden class from body.
   * Also stops the class guard so Orbe can manage orbe--opened freely.
   */
  function showOrbeModal() {
    stopOrbeClassGuard();
    document.body.classList.remove('orbe-modal-hidden');
  }

  /**
   * Hide Orbe modal by adding the hidden class to body
   */
  function hideOrbeModal() {
    document.body.classList.add('orbe-modal-hidden');
  }

  /**
   * Main Logic: Determine if modal should be shown based on country and market
   * @param {string} country - Detected country code
   * @param {Map<string, string>} excludedMap - Map of excluded countries
   * @returns {boolean} True if modal should be shown
   */
  function shouldShowOrbeModal(country, excludedMap) {
    // Early validation: If no country detected OR country is NOT in excluded list,
    // return false (don't show modal).
    // 
    // Reason: This function only evaluates modal display for EXCLUDED countries.
    // If country is not excluded, it will be automatically redirected and doesn't need the modal.
    // 
    // Example: If country is "us" and it's in excludedMap as "us:row":
    //   - If current market is "row": return false (user is in correct market, no modal needed)
    //   - If current market is "es": return true (user should see modal to switch to "row" market)
    if (!country || !excludedMap.has(country)) {
      return false;
    }

    const expectedMarket = excludedMap.get(country);
    const currentMarket = config.currentMarketHandle.toLowerCase();

    // Show modal if user is in excluded country but not in correct market
    return expectedMarket !== currentMarket;
  }

  /**
   * Main Execution Logic
   */

  /**
   * Main function that orchestrates the redirection logic
   */
  async function init() {
    // Step 1: Check if redirection is enabled
    if (!config.enabled) {
      // Script disabled - do nothing, let Orbe behave by default
      return;
    }

    // Start guard immediately: remove orbe--opened if Orbe adds it while modal is hidden.
    // Stops automatically when showOrbeModal() is called (modal becomes truly visible).
    startOrbeClassGuard();

    // Step 2: Check for manual selection cookie (ABSOLUTE PRIORITY)
    const manualCountry = getCookie(ORBE_COOKIE_NAME);
    if (manualCountry) {
      // Cookie exists - respect manual selection, do nothing
      // Modal should remain hidden (already set by body class)
      return;
    }

    // Step 3: Attempt geolocation (only if no cookie)
    const detectedCountry = await getUserCountry();

    // Step 4: Parse excluded countries configuration
    const excludedMap = parseExcludedCountries(config.excludedCountries);
    const currentMarket = config.currentMarketHandle.toLowerCase();

    // Step 5: Apply redirection and modal rules
    if (!detectedCountry) {
      // No geolocation available
      // Show modal only if on ES market (per acceptance criteria)
      if (currentMarket === 'es') {
        showOrbeModal();
      }
      // Otherwise keep modal hidden (ROW market)
      return;
    }

    // Country detected - check if it's excluded
    if (excludedMap.has(detectedCountry)) {
      // Country is excluded - no automatic redirection
      // Check if modal should be shown
      if (shouldShowOrbeModal(detectedCountry, excludedMap)) {
        showOrbeModal();
      }
      // Otherwise keep modal hidden
      return;
    }

    // Country is NOT excluded - apply redirection rules
    // Only redirect if on ES market (per acceptance criteria)
    if (currentMarket === 'es') {
      redirectWithCountry(detectedCountry);
    }
    // If on ROW market, do nothing (keep modal hidden)
  }

  // Execute when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }
})();

